import debounce from "lodash/debounce";
import { useEffect, useRef, useState } from "react";

import useSuccessRetryPaymentListenerStore from "./store";
import { useWebUserStore } from "../../../../../CommonStores/user/users";
import {
  getErrorMsg,
  useEffectOnlyOnce,
} from "../../../../../../Common/utils/common";
import { isNull } from "lodash";
import { usePurchaseV2Store } from "../../store";
import useWLPromoStore from "../../../../../components/WLPromoField/store";
import useSuperGiveAwayStore from "../../../../../CommonStores/giveaways/store";
import { ViewWLSubscriptionsTiers } from "../../../../../../Common/models/subscriptions";
import { getStripePriceValueWithPromo } from "../../../../../services/promo";
import { pushKlaviyoPurchaseEvent } from "../../../../../api/klaviyo";
import { bugNotif } from "../../../../../api/bugsnag";
import Stripe from "stripe";

const useSuccessRetryPaymentListenerProvider = () => {
  const { user } = useWebUserStore();

  const {
    inProcessRetryPayment,
    successfulPurchase,
    setSuccessfulPurchase,
    setInProcessRetryPayment,
  } = useSuccessRetryPaymentListenerStore();

  const { selectedProduct } = usePurchaseV2Store();

  const { ongoingGiveaway } = useSuperGiveAwayStore();

  const [subscriptionReported, setSubscriptionReported] = useState(false);
  const subscriptionReportedRef = useRef(false);
  subscriptionReportedRef.current = subscriptionReported;

  useEffectOnlyOnce(() => {
    const redirectStatusParam = new URLSearchParams(window.location.search).get(
      "redirect_status"
    );

    if (!!redirectStatusParam && redirectStatusParam === "succeeded") {
      setInProcessRetryPayment(true);

      // continue to success progress when succeeded from using stripe form
      setTimeout(
        () => {
          setSuccessfulPurchase(true);
        },
        56 * 30 + 1000
      );
    }
  });

  useEffect(() => {
    if (
      !!user &&
      !!user.latestStripeEvent &&
      !!inProcessRetryPayment &&
      !successfulPurchase &&
      (user.latestStripeEvent.type === "invoice.paid" ||
        (user.latestStripeEvent.type === "customer.subscription.updated" &&
          (user.latestStripeEvent.data.object as Stripe.Subscription).status ===
            "active"))
    ) {
      // show success animation
      setSuccessfulPurchase(true);
    }
  }, [user, inProcessRetryPayment, successfulPurchase]);

  useEffect(() => {
    if (!successfulPurchase) {
      return;
    }

    if (selectedProduct === undefined) {
      return;
    }

    if (isNull(ongoingGiveaway)) {
      return;
    }

    pushKlaviyoSuccessSubscription(
      selectedProduct,
      ongoingGiveaway.code || "N/A"
    );
  }, [selectedProduct, ongoingGiveaway, successfulPurchase]);

  const pushKlaviyoSuccessSubscription = debounce(
    async (selectedProduct: ViewWLSubscriptionsTiers, giveawayCode: string) => {
      try {
        const { validStripeCoupon, validPromoCode, validCouponCode } =
          useWLPromoStore.getState();

        const user = useWebUserStore.getState().user;
        if (!user) {
          throw new Error("No user found");
        }

        const { docId, price, name } = selectedProduct;
        let value =
          validStripeCoupon !== undefined && !isNull(validStripeCoupon)
            ? getStripePriceValueWithPromo(price, validStripeCoupon)
            : price;

        if (subscriptionReportedRef.current) {
          return;
        }

        await pushKlaviyoPurchaseEvent({
          orderEvent:
            validPromoCode || validCouponCode
              ? "FixMembershipWithPromo"
              : "FixMembershipWithoutPromo",
          orderType: "retryPayment",
          productId: docId,
          value,
          productName: name,
          promoCode: validPromoCode || validCouponCode || "",
          giveawayCode: giveawayCode || "",
          emailAddress: user.emailAddress,
          doubleForHalfOffAvailed: false,
        });

        setSubscriptionReported(true);
      } catch (error) {
        const { errorMsg, rawErrorMsg } = getErrorMsg(error);
        console.log("error sending metric", errorMsg);
        bugNotif("pushKlaviyoSuccessSubscription", rawErrorMsg);
        setSubscriptionReported(false);
      }
    },
    1000
  );
};

export default useSuccessRetryPaymentListenerProvider;
